// The Public Information Office's own moving things (registered into fx.ts; imported for its side effect by the PIO
// screens): the slips the interns carry, the reporter's telephone handset, the typewriter in the booth.
// Every effect is a pure function of the clock, like the rest of the world.
import type { CastEntry, FxEntry, Waypoint } from './assets';
import { registerFx, type FxContext } from './fx';
import { breath, castActors, hashStr } from './life';

interface PioFx extends FxEntry {
  who?: string;
  path?: Waypoint[];
  speed?: number;
  skin?: string;
  suit?: string;
  px?: number;
  py?: number;
}

const INK = '#160f1a';
const PAPER = '#fffbf2';
const RULE = '#a89f92';

function rect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string): void {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

/** The cast entry an effect follows, as an actor at this moment (null while out of sight). */
function follow(c: FxContext, who: string | undefined) {
  if (!who) return null;
  const e: CastEntry | undefined = c.m.cast?.find((k) => k.id === who);
  if (!e) return null;
  const [a] = castActors({ ...c.m, cast: [e] }, e.floor, c.now, c.animate);
  return a && a.kind === 'stand' ? a : null;
}

/** A slip opinion in an intern's hand on the way out (in profile, and at the side seen from behind); none coming back. */
function carry(ctx: CanvasRenderingContext2D, e: PioFx, c: FxContext): void {
  const a = follow(c, e.who);
  if (!a) return;
  const bob = a.bob ?? 0;
  if (a.view === 'front') {
    // coming back from the plaza: the slip has been delivered
    return;
  }
  if (a.view === 'back') {
    // seen from behind, the slip shows at the right hand
    rect(ctx, a.x + 5, a.y - 25 + bob, 4, 10, INK);
    rect(ctx, a.x + 6, a.y - 24 + bob, 2, 8, PAPER);
  } else if (a.view === 'l' || a.view === 'r') {
    const dir = a.view === 'r' ? 1 : -1;
    const swing = a.step !== undefined ? [0, 1, 0, -1][a.step % 4] : 0;
    const x = a.x + dir * (3 + swing) - (dir < 0 ? 3 : 0);
    rect(ctx, x, a.y - 26, 4, 10, INK);
    rect(ctx, x + 1, a.y - 25, 2, 8, PAPER);
  }
}

/** A reporter in profile with the receiver at the ear, the curly cord down to the telephone on the desk. */
function phone(ctx: CanvasRenderingContext2D, e: PioFx, c: FxContext, h: number): void {
  const bob = c.animate ? breath(c.now, hashStr(e.who ?? '')) : 0;
  // seated in profile facing right (atlas side.l): the head spans x-6..x+6, the ear near x, the mouth near x+5
  const x = e.x; const y = e.y + bob;
  // the far forearm raised from the elbow to the jaw, in the jacket's shadow tone
  for (let k = 0; k < 9; k += 1) rect(ctx, x - 4 + Math.round(k / 4), y - 12 - k, 3, 1, e.suit ?? '#4b2015');
  rect(ctx, x - 5, y - 20, 1, 8, INK);
  // the receiver: earpiece at the ear, the grip in the hand, mouthpiece at the chin
  rect(ctx, x - 2, y - 29, 4, 5, INK);
  rect(ctx, x - 1, y - 28, 2, 3, '#3c3749');
  rect(ctx, x + 1, y - 25, 2, 5, INK);
  rect(ctx, x + 2, y - 21, 4, 3, INK);
  rect(ctx, x - 2, y - 24, 3, 3, e.skin ?? '#efc4a2');
  rect(ctx, x - 3, y - 24, 1, 3, INK);
  // the cord, sagging from the mouthpiece to the desk telephone (it sways while they talk)
  const px = e.px ?? x + 20; const py = e.py ?? y;
  const sway = c.animate ? Math.round(Math.sin(c.now / 900 + (h % 7))) : 0;
  const n = 14;
  for (let i = 1; i < n; i += 1) {
    const t = i / n;
    const cx = x + 4 + (px - x - 4) * t;
    const cy = y - 18 + (py - y + 18) * t + Math.sin(t * Math.PI) * (6 + sway);
    if (i % 2) rect(ctx, cx, cy, 1, 1, '#2a2634');
  }
}

/** Typing in bursts: the carriage steps along, the bell, the return; the sheet rises a line at a time. */
function typewriter(ctx: CanvasRenderingContext2D, e: PioFx, c: FxContext, h: number): void {
  const t = c.animate ? c.now + (h % 9000) : 0;
  const cycle = 9000;
  const within = t % cycle;
  const typing = c.animate && within < 6200;
  const keys = typing ? Math.floor(within / 130) : 0;
  const lines = Math.floor(t / cycle) % 4;
  const pos = typing ? Math.min(9, Math.floor(keys / 3)) : 0;
  const x = e.x; const y = e.y;
  // the sheet in the platen
  rect(ctx, x + 2 - pos, y - 14 - lines, 9, 8 + lines, INK);
  rect(ctx, x + 3 - pos, y - 13 - lines, 7, 7 + lines, PAPER);
  for (let l = 0; l < lines + 2; l += 1) rect(ctx, x + 4 - pos, y - 8 - l * 2, l === 0 ? Math.min(5, keys % 6) : 5, 1, RULE);
  // the carriage
  rect(ctx, x - 1 - pos, y - 7, 16, 2, INK);
  rect(ctx, x - pos, y - 7, 14, 1, '#565068');
  // a key going down
  if (typing && keys % 2 === 0) rect(ctx, x + 2 + ((keys * 7) % 9), y - 3, 1, 1, INK);
  // the hands hovering over the keys
  if (typing) {
    rect(ctx, x - 1 + (keys % 3), y - 4 - (keys % 2), 2, 1, '#c4865b');
    rect(ctx, x + 6 + ((keys + 1) % 3), y - 4 - ((keys + 1) % 2), 2, 1, '#c4865b');
  }
}

registerFx('pio_carry', (ctx, e, c) => carry(ctx, e as PioFx, c), 'air');
registerFx('pio_phone', (ctx, e, c, h) => phone(ctx, e as PioFx, c, h), 'air');
registerFx('pio_type', (ctx, e, c, h) => typewriter(ctx, e as PioFx, c, h), 'air');
